'use client';

import { useEffect, useMemo, useRef } from 'react';

/**
 * Shared timeout-tracking utility for effect-owning landing-page sections.
 *
 * Each section's useEffect schedules timers via `setT(fn, ms)`. The returned
 * ids are collected in a Set; unmount auto-cancels every in-flight timer.
 * Avoids the "forgot a clearTimeout in the cleanup" bug and dedupes the same
 * boilerplate across every section.
 *
 * Returns { setT, clearAll } stable across renders (setT can be captured once
 * at effect entry without a dependency-array entry).
 *
 *   const { setT, clearAll } = useTrackedTimeouts();
 *   useEffect(() => {
 *     setT(() => foo(), 1000);
 *     return clearAll;        // or just let unmount handle it
 *   }, []);
 */
export function useTrackedTimeouts() {
  const pendingRef = useRef(null);
  if (pendingRef.current === null) {
    pendingRef.current = new Set();
  }

  const api = useMemo(
    () => ({
      setT(fn, ms) {
        const id = setTimeout(() => {
          pendingRef.current.delete(id);
          fn();
        }, ms);
        pendingRef.current.add(id);
        return id;
      },
      clearAll() {
        pendingRef.current.forEach((id) => clearTimeout(id));
        pendingRef.current.clear();
      },
    }),
    [],
  );

  useEffect(() => {
    return () => {
      pendingRef.current.forEach((id) => clearTimeout(id));
      pendingRef.current.clear();
    };
  }, []);

  return api;
}
